import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { insurerType } from 'constants/enum';
import {
  setInitialCheckedValue as setInitialCheckedValueAIG,
  setLoadingForAIG,
  setPreselectRejected as setPreselectRejectedAIG
} from 'container/screens/AddOnAIG/Logic/AddOnAIG.reducer';
import {
  setInitialCheckedValue,
  setLoadingForZurich,
  setPreselectRejected
} from 'container/screens/AddOnZurich/Logic/AddOnZurich.reducer';
import { setFieldValue } from 'container/screens/CoverageSumInsured/Logic/CoverageSumInsured.reducer';
import { logError } from 'util/helper';

const baseUrl =
  process.env.NODE_ENV === 'development'
    ? '/services'
    : `${process.env.REACT_APP_SDM_URL}/services/simedarby/api`;

const baseQuery = fetchBaseQuery({ baseUrl });

const customBaseQuery = async (args, api, extraOptions) => {
  args.headers = new Headers(args.headers);
  args.headers.set('Content-Type', 'application/json');

  return baseQuery(args, api, extraOptions);
}

export const Services = createApi({
  reducerPath: 'Services',
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    checkInsuranceEligibility: builder.mutation({
      query: (data) => ({
        url: '/onboarding/',
        method: 'POST',
        body: data
      }),
      onQueryStarted: async (data, { dispatch, queryFulfilled }) => {
        try {
          const resp = await queryFulfilled;
          const sumInsured =
            resp?.data?.vix_response?.response?.vehicle_details
              ?.ism_insured_value;
          dispatch(setFieldValue(sumInsured));
        } catch (error) {
          logError(error, 'check landing page eligibility error');
        }
      }
    }),
    chooseSumInsured: builder.mutation({
      query: (data) => {
        const { application_id: applicationId } = data;
        return {
          url: `/onboarding/${applicationId}/quotation`,
          method: 'POST',
          body: data
        };
      },
      onQueryStarted: async (data, { dispatch, queryFulfilled }) => {
        if (data.insurer_code[0] === 'ZT') {
          try {
            dispatch(setLoadingForZurich({ isLoading: true }));
            const priceData = await queryFulfilled;
            const selectedAddOnsZurich =
              priceData?.data?.response?.ZT?.extra_coverage?.extra_block;
            const mappedAddOnsZurich = selectedAddOnsZurich?.map((addOn) => {
              return {
                code: addOn[0].cover_code,
                availability: addOn[0].cover_availability,
                premium: addOn[0].cover_premium
              };
            });
            dispatch(setInitialCheckedValue(mappedAddOnsZurich));
          } catch (error) {
            dispatch(setPreselectRejected(true));
            logError(error, 'onboarding preselected add ons zurich error');
          } finally {
            dispatch(setLoadingForZurich({ isLoading: false }));
          }
        }
        if (data.insurer_code[0] === 'AIG') {
          dispatch(setLoadingForAIG({ isLoading: true }));
          try {
            const priceData = await queryFulfilled;
            const selectedAddOnsAIG =
              priceData?.data?.response?.AIG?.extra_coverage?.extra_block;
            const mappedAddOnsAIG = selectedAddOnsAIG?.map((addOn) => {
              return {
                code: addOn[0].cover_code,
                availability: addOn[0].cover_availability,
                premium: addOn[0].cover_premium
              };
            });
            dispatch(setInitialCheckedValueAIG(mappedAddOnsAIG));
          } catch (error) {
            dispatch(setPreselectRejectedAIG(true));
            logError(error, 'onboarding preselected add ons error');
          } finally {
            dispatch(setLoadingForAIG({ isLoading: false }));
          }
        }
      }
    }),
    updateSelectedAddOns: builder.mutation({
      query: (data) => {
        const { insurer, quotationNo, ...updatePrice } = data;
        return {
          url: `/quotation/${quotationNo}`,
          method: 'PATCH',
          body: updatePrice
        };
      },
      onQueryStarted: async (data, { dispatch, queryFulfilled }) => {
        if (data?.insurer?.toLowerCase() === insurerType.ZT.toLowerCase()) {
          dispatch(setLoadingForZurich({ isLoading: true }));
          try {
            const priceData = await queryFulfilled;
            if (priceData?.data?.response?.ZT) {
              const selectedAddOnsZurich =
                priceData?.data?.response?.ZT?.extra_coverage?.extra_block;
              const mappedAddOnsZurich = selectedAddOnsZurich?.map((addOn) => {
                return {
                  code: addOn[0].cover_code,
                  availability: addOn[0].cover_availability,
                  premium: addOn[0].cover_premium
                };
              });
              dispatch(setInitialCheckedValue(mappedAddOnsZurich));
            }
          } catch (error) {
            dispatch(setPreselectRejected(true));
            logError(error, 'update selected add ons zurich error');
          } finally {
            dispatch(setLoadingForZurich({ isLoading: false }));
          }
        }
        if (data?.insurer?.toLowerCase() === insurerType.AIG.toLowerCase()) {
          dispatch(setLoadingForAIG({ isLoading: true }));
          try {
            const priceData = await queryFulfilled;
            if (priceData?.data?.response?.AIG) {
              const selectedAddOnsAIG =
                priceData?.data?.response?.AIG?.extra_coverage?.extra_block;
              const mappedAddOnsAIG = selectedAddOnsAIG?.map((addOn) => {
                return {
                  code: addOn[0].cover_code,
                  availability: addOn[0].cover_availability,
                  premium: addOn[0].cover_premium
                };
              });
              dispatch(setInitialCheckedValueAIG(mappedAddOnsAIG));
            }
          } catch (error) {
            dispatch(setPreselectRejectedAIG(true));
            logError(error, 'update selected add ons error');
          } finally {
            dispatch(setLoadingForAIG({ isLoading: false }));
          }
        }
      }
    }),
    requestOrder: builder.mutation({
      query: (data) => {
        const { quotationNo, ...requestOrder } = data;
        return {
          url: `/quotation/${quotationNo}/order`,
          method: 'POST',
          body: requestOrder
        };
      }
    }),
    coverNoteGeneration: builder.mutation({
      query: (data) => {
        const { orderNo, ...coverNote } = data;
        return {
          url: `/order/${orderNo}/cover_note`,
          method: 'POST',
          body: coverNote
        };
      }
    }),
    resumeJourney: builder.mutation({
      query: (data) => {
        const { applicationId } = data;
        return {
          url: `${process.env.REACT_APP_SDM_URL}/services/onboarding/api/application/${applicationId}`,
          method: 'GET'
        };
      }
    }),
    saveApplication: builder.mutation({
      query: (data) => {
        return {
          url: `${process.env.REACT_APP_SDM_URL}/services/onboarding/api/application`,
          method: 'PUT',
          body: data
        };
      }
    }),
    getVehicleInformation: builder.mutation({
      query: (applicationId) => {
        return {
          url: `/onboarding/${applicationId}`,
          method: 'GET'
        };
      }
    })
  })
});

export const {
  useCheckInsuranceEligibilityMutation,
  useChooseSumInsuredMutation,
  useCoverNoteGenerationMutation,
  useGetVehicleInformationMutation,
  useRequestOrderMutation,
  useResumeJourneyMutation,
  useSaveApplicationMutation,
  useUpdateSelectedAddOnsMutation
} = Services;
